import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { adminApi } from "../../../lib/admin";
import type { AdminGigIssueScope } from "../../../types/adminTypes";
import type { PropertyPredicateDto } from "../../../types/gigTypes";
import { endOfLocalDay, parseLocalDateInput, startOfLocalDay, toIsoTimestamp } from "../../../utils/DateUtils";
import { hasRole } from "../../../utils/AuthUtils";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import AdminIssuesTable from "./AdminIssuesTable";

const DEFAULT_PAGE_SIZE = 10;

const AdminIssuesPanel = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [issueScope, setIssueScope] = useState<"" | AdminGigIssueScope>("");
  const [createdFromDate, setCreatedFromDate] = useState("");
  const [createdToDate, setCreatedToDate] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const debouncedSearch = useDebouncedValue(search, 300);
  const canWrite = hasRole(user?.roles, "ADMIN_WRITE");

  const predicates: PropertyPredicateDto[] = [];
  if (debouncedSearch.trim().length > 0) {
    predicates.push({ propertyName: "search", propertyValue: debouncedSearch.trim(), operation: "CONTAINS" });
  }
  if (status.trim().length > 0) {
    predicates.push({ propertyName: "status", propertyValue: status.trim(), operation: "EQUALS" });
  }
  if (issueScope.trim().length > 0) {
    predicates.push({ propertyName: "issueScope", propertyValue: issueScope.trim(), operation: "EQUALS" });
  }
  const createdFrom = parseLocalDateInput(createdFromDate);
  const createdTo = parseLocalDateInput(createdToDate);
  if (createdFrom) {
    predicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(startOfLocalDay(createdFrom)), operation: "TIMESTAMP_FROM" });
  }
  if (createdTo) {
    predicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(endOfLocalDay(createdTo)), operation: "TIMESTAMP_TO" });
  }

  const issuesQuery = useQuery({
    queryKey: ["admin-issues", { page, pageSize, debouncedSearch, status, issueScope, createdFromDate, createdToDate }],
    queryFn: () =>
      adminApi.listIssues({
        predicates,
        page: {
          number: page,
          size: pageSize,
          numberOfElements: null,
          hasNext: false,
          hasPrevious: false,
          first: false,
          last: false,
          sortBy: [{ direction: "DESC", propertyName: "createdAt" }],
        },
      }),
    placeholderData: (previousData) => previousData,
  });

  const error = issuesQuery.error ? apiErrorToMessage(issuesQuery.error, "Failed to load gig issues.") : null;

  return (
    <div>
      {error ? <p className="mb-4 text-sm font-medium text-red-600">{error}</p> : null}
      <AdminIssuesTable
        issues={issuesQuery.data?.issues ?? []}
        isLoading={issuesQuery.isLoading}
        canWrite={canWrite}
        search={search}
        status={status}
        issueScope={issueScope}
        createdFromDate={createdFromDate}
        createdToDate={createdToDate}
        pageSize={pageSize}
        page={page}
        resultsCount={issuesQuery.data?.page.numberOfElements ?? 0}
        hasNextPage={issuesQuery.data?.page.hasNext ?? false}
        hasPreviousPage={issuesQuery.data?.page.hasPrevious ?? false}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        onStatusChange={(value) => {
          setStatus(value);
          setPage(0);
        }}
        onIssueScopeChange={(value) => {
          setIssueScope(value);
          setPage(0);
        }}
        onCreatedFromDateChange={(value) => {
          setCreatedFromDate(value);
          setPage(0);
        }}
        onCreatedToDateChange={(value) => {
          setCreatedToDate(value);
          setPage(0);
        }}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(0);
        }}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminIssuesPanel;
